/**
 * Presentation helpers for the call-logs UI — phone/duration/time formatting and
 * the shared colour language for direction, status, sentiment and resolution.
 */

import {
  Bot,
  PhoneCall,
  PhoneForwarded,
  PhoneIncoming,
  PhoneMissed,
  PhoneOff,
  PhoneOutgoing,
  Voicemail,
  type LucideIcon,
} from 'lucide-react';
import type {
  CallDirection,
  CallLogDetail,
  CallLogItem,
  CallResolution,
  CallSentiment,
} from './types';

/** Pretty-print an E.164 US number as (415) 555-0132; leave others as-is. */
export function formatPhone(raw?: string | null): string {
  if (!raw) return 'Unknown';
  const digits = raw.replace(/[^\d]/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    const d = digits.slice(1);
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

/** The calling service returns incoming/outgoing; map to our inbound/outbound. */
export function normalizeDirection(d?: string | null): CallDirection {
  return d === 'outbound' || d === 'outgoing' ? 'outbound' : 'inbound';
}

/** The other party's number: caller for inbound, callee for outbound. */
export function counterpartNumber(item: CallLogItem): string {
  return normalizeDirection(item.direction) === 'inbound' ? item.from : item.to;
}

/**
 * The contact's real name, or null when all we have is a phone number. A
 * `customerName` that is just digits/phone punctuation isn't a name — the number
 * already shows in its own column — so we treat it as unknown.
 */
export function resolvedContactName(item: CallLogItem): string | null {
  const name = item.customerName?.trim();
  if (!name) return null;
  if (/^[\d\s()+\-.]+$/.test(name)) return null;
  return name;
}

/**
 * Our side of the call: the business number the customer rang (inbound), or
 * the line we dialled out from (outbound).
 */
export function businessLine(item: CallLogItem): string {
  return normalizeDirection(item.direction) === 'inbound' ? item.to : item.from;
}

const AGENT_ID = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|[0-9a-f]{24})$/i;

/**
 * The teammate who took the call, or null. `answeredByName` is the name
 * operations resolved; the other two are what older calling-service rows
 * stored themselves — and `pickedBy` can hold an agent id rather than a name,
 * which must never be shown as one.
 */
export function answeredByLabel(item: CallLogItem & { answerBy?: string | null }): string | null {
  for (const value of [item.answeredByName, item.teamMemberName, item.pickedBy, item.answerBy]) {
    const name = value?.trim();
    if (name && !AGENT_ID.test(name)) return name;
  }
  return null;
}

/** Best display name for a call: the real contact name, else the formatted number. */
export function callDisplayName(item: CallLogItem): string {
  return resolvedContactName(item) ?? formatPhone(counterpartNumber(item));
}

/** Two-letter initials for the avatar. */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '#';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Human talk time: "0:42", "3:07", "1:02:30". */
export function formatDuration(seconds?: number | null): string {
  if (!seconds || seconds < 0) return '0:00';
  const s = Math.floor(seconds % 60);
  const m = Math.floor((seconds / 60) % 60);
  const h = Math.floor(seconds / 3600);
  const mm = m.toString().padStart(h > 0 ? 2 : 1, '0');
  const ss = s.toString().padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Compact relative time: "just now", "12m", "3h", "Yesterday", "Mar 4". */
export function formatRelativeTime(iso?: string | null): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diffMs = Date.now() - then;
  const min = Math.round(diffMs / 60_000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.round(hr / 24);
  if (day === 1) return 'Yesterday';
  if (day < 7) return `${day}d`;
  const wk = Math.round(day / 7);
  if (wk < 5) return `${wk}w`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo}mo`;
  return `${Math.round(day / 365)}y`;
}

export type CallKind = 'missed' | 'voicemail' | 'inbound' | 'outbound';

export interface DirectionMeta {
  kind: CallKind;
  Icon: LucideIcon;
  label: string;
  /** Tailwind text colour token for the glyph. */
  fg: string;
  /** Tailwind background tint for the glyph chip. */
  bg: string;
}

const isVoicemail = (status?: string | null) =>
  !!status && status.toLowerCase().includes('voicemail');

/** The glyph + colour language for a call's direction/outcome. */
export function directionMeta(item: CallLogItem): DirectionMeta {
  if (item.isMissedCall) {
    return {
      kind: 'missed',
      Icon: PhoneMissed,
      label: 'Missed',
      fg: 'text-rose-600 dark:text-rose-400',
      bg: 'bg-rose-500/10',
    };
  }
  if (isVoicemail(item.callStatus)) {
    return {
      kind: 'voicemail',
      Icon: Voicemail,
      label: 'Voicemail',
      fg: 'text-amber-600 dark:text-amber-400',
      bg: 'bg-amber-500/10',
    };
  }
  if (normalizeDirection(item.direction) === 'outbound') {
    return {
      kind: 'outbound',
      Icon: PhoneOutgoing,
      label: 'Outgoing',
      fg: 'text-sky-600 dark:text-sky-400',
      bg: 'bg-sky-500/10',
    };
  }
  return {
    kind: 'inbound',
    Icon: PhoneIncoming,
    label: 'Incoming',
    fg: 'text-emerald-600 dark:text-emerald-400',
    bg: 'bg-emerald-500/10',
  };
}

export type BadgeTone = 'neutral' | 'positive' | 'warning' | 'danger' | 'info';

/** Tailwind classes for a tinted status/label chip by tone. */
export function toneClasses(tone: BadgeTone): string {
  switch (tone) {
    case 'positive':
      return 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 ring-emerald-500/20';
    case 'warning':
      return 'bg-amber-500/10 text-amber-700 dark:text-amber-300 ring-amber-500/20';
    case 'danger':
      return 'bg-rose-500/10 text-rose-700 dark:text-rose-300 ring-rose-500/20';
    case 'info':
      return 'bg-sky-500/10 text-sky-700 dark:text-sky-300 ring-sky-500/20';
    default:
      return 'bg-muted text-muted-foreground ring-border';
  }
}

/** Soft, ring-less status pill (bg + text only) — the flat "Cold/Hot Lead" look. */
export function softToneClasses(tone: BadgeTone): string {
  switch (tone) {
    case 'positive':
      return 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300';
    case 'warning':
      return 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300';
    case 'danger':
      return 'bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300';
    case 'info':
      return 'bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300';
    default:
      return 'bg-muted text-muted-foreground';
  }
}

/** A single solid dot colour per tone — the only colour in a table row. */
export function dotClass(tone: BadgeTone): string {
  switch (tone) {
    case 'positive':
      return 'bg-emerald-500';
    case 'warning':
      return 'bg-amber-500';
    case 'danger':
      return 'bg-rose-500';
    case 'info':
      return 'bg-sky-500';
    default:
      return 'bg-muted-foreground/40';
  }
}

/**
 * The single on-theme linear gradient for every contact avatar: a soft slate
 * gradient with white initials. Fixed hexes so it reads the same in light and
 * dark mode.
 */
export const AVATAR_GRADIENT = 'bg-gradient-to-br from-[#5A6472] to-[#3D4655] text-white';

export interface CallOutcome {
  Icon: LucideIcon;
  /** One logical word combining direction + result, e.g. "Incoming", "Missed". */
  label: string;
  tone: BadgeTone;
}

/**
 * A single logical status for a call — folds direction (incoming/outgoing) and
 * result into ONE badge, so the table needs only one status column instead of
 * separate "type" and "status" chips.
 *
 * Every CS status label (Answered, Missed, Rejected, Ringing, Voicemail,
 * Transferred, AI Handled) is matched explicitly BEFORE the direction fallback,
 * so a row never shows "Incoming" when its real status is e.g. Rejected. Legacy
 * provider values (no-answer / busy / failed / canceled) are still recognised.
 * Only Answered/unknown fall through to the plain direction label.
 */
export function callOutcome(item: CallLogItem): CallOutcome {
  const dir = normalizeDirection(item.direction);
  const s = (item.callStatus ?? '').trim().toLowerCase();

  // Voicemail first — a voicemail row can also carry the missed flag.
  if (s.includes('voicemail')) return { Icon: Voicemail, label: 'Voicemail', tone: 'warning' };
  // Missed: the explicit status or the isMissedCall flag.
  if (item.isMissedCall || s === 'missed')
    return { Icon: PhoneMissed, label: 'Missed', tone: 'danger' };
  if (s.includes('reject')) return { Icon: PhoneOff, label: 'Rejected', tone: 'danger' };
  if (s.includes('no') && s.includes('answer'))
    return { Icon: PhoneMissed, label: 'No answer', tone: 'danger' };
  if (s.includes('busy')) return { Icon: PhoneOff, label: 'Busy', tone: 'danger' };
  if (s.includes('fail')) return { Icon: PhoneMissed, label: 'Failed', tone: 'danger' };
  if (s.includes('cancel')) return { Icon: PhoneOff, label: 'Canceled', tone: 'neutral' };
  if (s === 'ringing') return { Icon: PhoneCall, label: 'Ringing', tone: 'neutral' };
  if (s.includes('transfer')) return { Icon: PhoneForwarded, label: 'Transferred', tone: 'info' };
  if (s.includes('ai handled')) return { Icon: Bot, label: 'AI Handled', tone: 'info' };

  // Answered / completed / unknown — the meaningful status is the direction.
  return dir === 'outbound'
    ? { Icon: PhoneOutgoing, label: 'Outgoing', tone: 'info' }
    : { Icon: PhoneIncoming, label: 'Incoming', tone: 'positive' };
}

/** Map a provider call status to a chip tone + tidy label. */
export function statusTone(status?: string | null): {
  tone: BadgeTone;
  label: string;
} {
  const s = (status ?? '').toLowerCase();
  if (!s) return { tone: 'neutral', label: '—' };
  if (s.includes('answer') && !s.includes('no')) return { tone: 'positive', label: 'Answered' };
  if (s.includes('complete')) return { tone: 'positive', label: 'Completed' };
  if (s.includes('voicemail')) return { tone: 'warning', label: 'Voicemail' };
  if (s.includes('transfer')) return { tone: 'info', label: 'Transferred' };
  if (s.includes('no') && s.includes('answer')) return { tone: 'danger', label: 'No answer' };
  if (s.includes('busy')) return { tone: 'danger', label: 'Busy' };
  if (s.includes('fail')) return { tone: 'danger', label: 'Failed' };
  if (s.includes('cancel')) return { tone: 'neutral', label: 'Canceled' };
  return { tone: 'neutral', label: status ?? '—' };
}

export function sentimentTone(s?: CallSentiment | null): BadgeTone {
  if (s === 'Positive') return 'positive';
  if (s === 'Negative') return 'danger';
  return 'neutral';
}

export function resolutionTone(r?: CallResolution | null): BadgeTone {
  if (r === 'Resolved') return 'positive';
  if (r === 'Escalated') return 'danger';
  return 'warning';
}

/** Coerce a Telnyx stat (often a string) to a finite number, or undefined. */
export function toNum(v?: number | string | null): number | undefined {
  if (v == null || v === '') return undefined;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : undefined;
}

/** Telnyx MOS → a quality label + tone (5 = perfect, <3.6 = poor). */
export function mosQuality(
  mos?: number | string | null,
): { label: string; tone: BadgeTone } | null {
  const n = toNum(mos);
  if (n === undefined) return null;
  if (n >= 4.3) return { label: 'Excellent', tone: 'positive' };
  if (n >= 4.0) return { label: 'Good', tone: 'positive' };
  if (n >= 3.6) return { label: 'Fair', tone: 'warning' };
  return { label: 'Poor', tone: 'danger' };
}

export const DIRECTIONS: { value: 'all' | CallDirection; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'inbound', label: 'Incoming' },
  { value: 'outbound', label: 'Outgoing' },
];

/**
 * The call's transcript as plain text when it only exists that way. On-demand
 * transcription writes `transcriptionSummary` and leaves `transcription[]`
 * empty, so a transcribed call can have text and no lines.
 */
export function plainTranscript(call: CallLogDetail): string | null {
  return call.transcriptionSummary?.trim() || null;
}

/** Whether there is any transcript to read, or for an AI insight to summarize. */
export function hasTranscript(call: CallLogDetail): boolean {
  return (
    Boolean(plainTranscript(call)) ||
    (call.transcription ?? []).some((line) => Boolean(line.text?.trim()))
  );
}
